/**
 * Ticket Lifecycle v2 — Slice 5D-5
 *
 * Conservative implicit advisor takeover. When a FOH-capable user
 * performs an advisor-authoritative action (slice 5D-6 cohort 1:
 * `invoice_sent`, `customer_approval_received`) on a ticket whose
 * current advisor is a *different* user, this hook fires a
 * `reassign_within_foh` custody transition so the actor becomes the
 * new advisor of record.
 *
 * Gating contract (ALL must be true for the takeover to fire):
 *
 *   1. `advisorImplicitTakeover` feature flag is ON.
 *   2. The trigger action kind is in the closed allow-list
 *      `IMPLICIT_TAKEOVER_ALLOWED_ACTION_KINDS`. View/open/read events
 *      are NEVER allowed.
 *   3. The actor (`auth.user.id`) is known.
 *   4. The actor's `membership.role` is in
 *      `IMPLICIT_TAKEOVER_FOH_CAPABLE_ROLES` (OWNER / MANAGER /
 *      ORG_ADMIN / SERVICE_WRITER — the same set used for the
 *      slice-5D-1 `canReassignAdvisor` permission, mirroring the
 *      `transition-ticket-custody` edge function's role gate).
 *   5. The ticket has a `ticket_custody` row.
 *   6. `ticket_custody.custody_owner === 'FOH'`.
 *   7. `ticket_custody.holder_user_id` is set.
 *   8. `holder_user_id !== actor.id` (no self-takeover no-op).
 *
 * Note that gates 3–8 are enforced *here* (client-side) in addition to
 * the `transition-ticket-custody` edge function's existing role gate
 * + the `_apply_custody_transition` RPC's status validity check. This
 * is defense in depth — if a check fails client-side we skip silently;
 * if a check fails server-side the failure is logged and the primary
 * advisor action is unaffected.
 *
 * Non-blocking contract:
 *   - The returned function always resolves; errors are
 *     `console.warn`'d and never thrown to the caller.
 *   - The implementation bypasses `useTicketCustodyMutation`
 *     specifically to avoid that hook's success/error toasts —
 *     implicit takeover should be silent at the UI layer (the
 *     downstream custody-events query invalidation will re-render any
 *     visible custody surface without a noisy toast).
 *   - The advisor action that triggered the takeover always succeeds
 *     or fails independently — the takeover never gates it.
 *
 * Boundary: imports the supabase client + feature flag hook + auth
 * context. Does NOT import scheduling / timer / parking / rollover /
 * bay / invariants code. The drift detector boundary guard pins this.
 */
import React, { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import type { AdvisorActionKind } from '@/lib/tickets/lifecycle/types';
import {
  buildCustodyHandoffMetadata,
  IMPLICIT_TAKEOVER_REASON,
} from '@/lib/tickets/lifecycle/custodyHandoffReasons';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { buildCustodyOcisEvent } from '@/lib/tickets/lifecycle/custodyOcisEvents';

/**
 * Closed allow-list of advisor action kinds that may trigger implicit
 * takeover. Pinned by the drift detector. Adding a new kind requires
 * the same scrutiny as the slice 5D-6 emit-site cohort — and must be
 * advisor-authoritative (NOT a view/open/read or technician action).
 *
 * Today: the two slice-5D-6 cohort-1 kinds. Notably excludes
 * `ticket_accepted` (already a custody event) and `kicked_back_to_tech`
 * (also a custody event), as well as all technician / view / timer /
 * scheduling kinds.
 */
export const IMPLICIT_TAKEOVER_ALLOWED_ACTION_KINDS: ReadonlySet<AdvisorActionKind> =
  new Set<AdvisorActionKind>([
    'invoice_sent',
    'customer_approval_received',
  ]);

/**
 * Roles permitted to *be the new holder* via implicit takeover. Mirrors
 * the slice-5D-1 `canReassignAdvisor` set. Notably excludes
 * `TECHNICIAN` (advisor reassignment is not a tech action) and
 * `DISPATCHER` (the `transition-ticket-custody` edge function's
 * `REQUIRE_ROLES` does not include them — the call would 403).
 */
export const IMPLICIT_TAKEOVER_FOH_CAPABLE_ROLES: ReadonlyArray<string> = [
  'OWNER',
  'MANAGER',
  'ORG_ADMIN',
  'SERVICE_WRITER',
];

export interface ImplicitAdvisorTakeoverArgs {
  ticketId: string;
  actionKind: AdvisorActionKind;
}

/**
 * Pure helper: given the gating inputs, decide whether implicit
 * takeover should be attempted. Returns `false` immediately if any
 * client-side gate fails. Exported for unit testing without a
 * Supabase client.
 *
 * This does NOT cover the runtime custody-state checks (gates 5–8 in
 * the module doc) — those require an async read of `ticket_custody`
 * and are enforced inside the hook proper.
 */
export function shouldAttemptImplicitAdvisorTakeover(args: {
  flagEnabled: boolean;
  actionKind: AdvisorActionKind;
  actorUserId: string | null | undefined;
  actorRole: string | null | undefined;
}): boolean {
  if (!args.flagEnabled) return false;
  if (!IMPLICIT_TAKEOVER_ALLOWED_ACTION_KINDS.has(args.actionKind)) return false;
  if (!args.actorUserId) return false;
  if (!args.actorRole) return false;
  if (!IMPLICIT_TAKEOVER_FOH_CAPABLE_ROLES.includes(args.actorRole)) return false;
  return true;
}

/**
 * Slice 5D-7: build the metadata payload for an *undo* of an implicit
 * takeover. The undo is itself a `reassign_within_foh` transition that
 * sends ownership back to the prior holder; the metadata makes it
 * cleanly distinguishable from the original takeover in the
 * `ticket_custody_events` log.
 *
 * Pure helper exported for unit testing. Keys are snake_case per the
 * DB convention of `ticket_custody_events.metadata`.
 */
export interface UndoTakeoverMetadataInput {
  originalTriggerActionKind: AdvisorActionKind;
  priorHolderUserId: string;
  actorUserId: string;
}

export function buildUndoTakeoverMetadata(
  input: UndoTakeoverMetadataInput,
): Record<string, unknown> {
  return {
    source: 'implicit_advisor_takeover_undo',
    original_trigger_action_kind: input.originalTriggerActionKind,
    previous_holder_user_id: input.priorHolderUserId,
    undo_actor_user_id: input.actorUserId,
  };
}

/**
 * Slice 5D-7: invalidate the four custody-related query keys the
 * takeover affects, so visible custody UI re-renders with the new
 * holder. Extracted to a small helper so both the success path and
 * the undo path share one invalidation contract.
 *
 * Pure(-ish) — only side effect is the `QueryClient` calls. No I/O.
 */
function invalidateCustodyQueries(qc: QueryClient, ticketId: string): void {
  qc.invalidateQueries({ queryKey: ['ticket-custody', ticketId] });
  qc.invalidateQueries({ queryKey: ['ticket-custody-events', ticketId] });
  qc.invalidateQueries({ queryKey: ['foh-custody-queue'] });
}

/**
 * Returns a fire-and-forget function. Callers should invoke it after a
 * successful advisor action emit; the function self-gates on all
 * conditions and silently skips when any guard fails.
 *
 * The function always resolves. It never throws. Failures are logged
 * via `console.warn`.
 */
export function useImplicitAdvisorTakeover() {
  const flagEnabled = useFeatureFlag('advisorImplicitTakeover');
  const { user, membership } = useAuth();
  const qc = useQueryClient();

  const actorUserId = user?.id ?? null;
  const actorRole = (membership?.role ?? null) as string | null;

  return useCallback(
    async (args: ImplicitAdvisorTakeoverArgs): Promise<void> => {
      // Synchronous gates (1–4). Cheap; no network.
      if (
        !shouldAttemptImplicitAdvisorTakeover({
          flagEnabled,
          actionKind: args.actionKind,
          actorUserId,
          actorRole,
        })
      ) {
        return;
      }

      try {
        // Async gates (5–8): read the current custody row.
        const { data: custody, error: custodyErr } = await (supabase as any)
          .from('ticket_custody')
          .select('custody_owner, custody_status, holder_user_id')
          .eq('ticket_id', args.ticketId)
          .maybeSingle();
        if (custodyErr) {
          console.warn(
            '[implicit-takeover] custody read failed (non-blocking)',
            custodyErr,
          );
          return;
        }
        if (!custody) return;
        if (custody.custody_owner !== 'FOH') return;
        if (!custody.holder_user_id) return;
        if (custody.holder_user_id === actorUserId) return;

        // Capture the prior holder *before* firing the transition so the
        // slice 5D-7 undo affordance has a reliable target user id even
        // if the custody row mutates again before the undo is clicked.
        const priorHolderUserId: string = custody.holder_user_id;

        // All gates passed. Fire the transition directly through the
        // edge function — we deliberately bypass `useTicketCustodyMutation`
        // to avoid its noisy success/error toasts. The slice 5D-7 soft
        // toast (below) is the only UI surfacing this slice introduces.
        //
        // Batch 1 (FOH timer handoff): metadata now follows the
        // standardized `CustodyHandoffMetadata` shape so this event is
        // queryable alongside manual / pause_kick / stop_kick handoffs.
        // The legacy `source: 'implicit_advisor_takeover'` literal +
        // `trigger_action_kind` key are preserved via `source` override
        // + `extra` so analytics queries that anchor on those keys do
        // not regress.
        const forwardMetadata = buildCustodyHandoffMetadata({
          trigger: 'implicit_takeover',
          reason: IMPLICIT_TAKEOVER_REASON,
          source: 'implicit_advisor_takeover',
          sourceComponent: 'useImplicitAdvisorTakeover',
          extra: {
            trigger_action_kind: args.actionKind,
          },
        });
        const { error: transitionErr } = await supabase.functions.invoke(
          'transition-ticket-custody',
          {
            body: {
              ticket_id: args.ticketId,
              event_kind: 'reassign_within_foh',
              to_user_id: actorUserId,
              reason: IMPLICIT_TAKEOVER_REASON,
              metadata: forwardMetadata,
            },
          },
        );
        if (transitionErr) {
          console.warn(
            '[implicit-takeover] transition failed (non-blocking)',
            transitionErr,
          );
          return;
        }

        // Mirror the cache invalidations `useTicketCustodyMutation`
        // performs in its own onSuccess, so any visible custody UI
        // (kebab, badge, FOH queue, lifecycle card) re-renders with
        // the new holder.
        invalidateCustodyQueries(qc, args.ticketId);

        // Batch 1 (FOH timer handoff): emit the custody event to the
        // OCIS timeline so implicit takeovers appear alongside other
        // custody activity. Fire-and-forget; failures only warn.
        void (async () => {
          try {
            const plan = buildCustodyOcisEvent({
              eventKind: 'reassign_within_foh',
              reason: IMPLICIT_TAKEOVER_REASON,
              metadata: forwardMetadata,
            });
            if (!plan) return;
            const { data: ticketRow } = await (supabase as any)
              .from('tickets')
              .select('shop_id')
              .eq('id', args.ticketId)
              .maybeSingle();
            if (!ticketRow?.shop_id) return;
            await logTicketEventToOcis({
              ticketId: args.ticketId,
              shopId: ticketRow.shop_id as string,
              eventType: plan.eventType,
              content: plan.content,
              payload: plan.payload,
            });
          } catch (e) {
            console.warn(
              '[implicit-takeover-ocis] timeline emit failed (non-blocking)',
              e,
            );
          }
        })();

        // ─── Slice 5D-7: soft actor toast + Undo affordance ───────
        //
        // The toast is the only UI surfacing implicit takeover. It is
        // intentionally SOFT — `default` variant (not destructive),
        // short copy, optional Undo action. Failures inside the undo
        // path log via `console.warn` only — the original advisor
        // action (e.g. invoice finalize) is already committed and
        // cannot be rolled back here. The undo path captures
        // `priorHolderUserId` + `actorUserId` + `args.actionKind` at
        // takeover-success time, so a stale custody row at undo-click
        // time does not cause the wrong user to be restored.
        const undoTakeover = async (): Promise<void> => {
          try {
            const { error: undoErr } = await supabase.functions.invoke(
              'transition-ticket-custody',
              {
                body: {
                  ticket_id: args.ticketId,
                  event_kind: 'reassign_within_foh',
                  to_user_id: priorHolderUserId,
                  reason: 'implicit_advisor_takeover_undo',
                  metadata: buildUndoTakeoverMetadata({
                    originalTriggerActionKind: args.actionKind,
                    priorHolderUserId,
                    actorUserId: actorUserId as string,
                  }),
                },
              },
            );
            if (undoErr) {
              console.warn(
                '[implicit-takeover-undo] transition failed (non-blocking)',
                undoErr,
              );
              return;
            }
            invalidateCustodyQueries(qc, args.ticketId);
          } catch (e) {
            console.warn(
              '[implicit-takeover-undo] unexpected error (non-blocking)',
              e,
            );
          }
        };

        // JSX is the sanctioned way to build a ToastActionElement — its element
        // type resolves to what `toast({ action })` expects. `React.createElement`
        // produces an element whose props generic doesn't match, which would
        // force an unsafe cast (this file is now .tsx so JSX is available).
        const undoAction: ToastActionElement = (
          <ToastAction
            altText="Undo advisor takeover"
            onClick={() => {
              void undoTakeover();
            }}
          >
            Undo
          </ToastAction>
        );

        toast({
          title: "You're now holding this ticket",
          description: 'Advisor ownership moved to you.',
          action: undoAction,
        });
      } catch (e) {
        console.warn('[implicit-takeover] unexpected error (non-blocking)', e);
      }
    },
    [flagEnabled, actorUserId, actorRole, qc],
  );
}
